import * as THREE from 'three';
import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { useGLTF, useAnimations } from '@react-three/drei';

interface CharacterProps {
    animation: string; // 'idle' | 'run' | 'jump' | 'attack'
    isEnemy?: boolean;
}

export const CharacterPlaceholder = ({ theme, animation, isEnemy = false }: { theme: 'medieval' | 'cyberpunk' | 'wizard' | 'archer' | 'manual_wizard' | 'manual_archer' } & CharacterProps) => {
    switch (theme) {
        case 'manual_wizard':
        case 'wizard':
            return <Wizard animation={animation} isEnemy={isEnemy} />;
        case 'manual_archer':
        case 'archer':
            return <Archer animation={animation} isEnemy={isEnemy} />;
        case 'cyberpunk':
            return <CyberRobot animation={animation} isEnemy={isEnemy} />;
        default:
            return <KnightModel animation={animation} isEnemy={isEnemy} />;
    }
};

const KnightModel = ({ animation }: CharacterProps) => {
    const group = useRef<THREE.Object3D>(null);
    const { scene, animations } = useGLTF('/assets/knight.glb');
    const model = useMemo(() => {
        const clone = scene.clone(true) as THREE.Object3D;
        clone.scale.setScalar(1.2);
        clone.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
            }
        });
        const box = new THREE.Box3();
        clone.updateWorldMatrix(true, true);
        clone.traverse((obj) => {
            const mesh = obj as THREE.Mesh;
            if (!mesh.isMesh || !mesh.geometry) return;
            mesh.geometry.computeBoundingBox();
            if (!mesh.geometry.boundingBox) return;
            const bb = mesh.geometry.boundingBox.clone();
            bb.applyMatrix4(mesh.matrixWorld);
            box.union(bb);
        });
        const yOffset = box.isEmpty() ? 0 : -box.min.y;
        clone.position.y += yOffset;
        return clone;
    }, [scene]);

    const { actions } = useAnimations(animations, group as unknown as THREE.Object3D);

    useEffect(() => {
        const keys = Object.keys(actions || {});
        if (!keys.length) return;

        const lower = animation.toLowerCase();
        const pick = (needle: string) => keys.find(k => k.toLowerCase().includes(needle));

        let actionName = pick('idle') || pick('stand') || keys[0];
        if (lower === 'run') actionName = pick('run') || pick('walk') || actionName;
        if (lower === 'attack') actionName = pick('attack') || pick('slash') || actionName;

        const action = actions[actionName];
        if (!action) return;
        action.reset().fadeIn(0.2).play();
        return () => {
            action.fadeOut(0.2);
        };
    }, [actions, animation]);

    return <primitive ref={group} object={model} />;
};

useGLTF.preload('/assets/knight.glb');

const CyberRobot = ({ animation, isEnemy }: CharacterProps) => {
    const group = useRef<THREE.Group>(null);
    const leftLeg = useRef<THREE.Mesh>(null);
    const rightLeg = useRef<THREE.Mesh>(null);

    useFrame((state) => {
        if (!group.current) return;
        const time = state.clock.getElapsedTime();

        // Hover Effect
        group.current.position.y = Math.sin(time * 5) * 0.05;

        if (animation === 'run') {
            // Tilt forward
            group.current.rotation.x = 0.2;
            // Legs
            if (leftLeg.current) leftLeg.current.rotation.x = Math.sin(time * 20) * 0.3;
            if (rightLeg.current) rightLeg.current.rotation.x = Math.sin(time * 20 + Math.PI) * 0.3;

        } else if (animation === 'attack') {
            // Attack Lunge
            group.current.position.z = Math.sin(time * 20) * 0.2;
            group.current.rotation.x = -0.2; // Lean forward
        } else {
            group.current.rotation.x = THREE.MathUtils.lerp(group.current.rotation.x, 0, 0.1);
            group.current.position.z = THREE.MathUtils.lerp(group.current.position.z, 0, 0.1);
        }
    });

    const neonColor = isEnemy ? "#f00" : "#0ff";

    return (
        <group ref={group}>
            {/* Core Node */}
            <mesh position={[0, 0.6, 0]} castShadow>
                <sphereGeometry args={[0.25, 16, 16]} />
                <meshStandardMaterial color="#111" />
            </mesh>
            {/* Neon Ring */}
            <mesh position={[0, 0.6, 0]}>
                <torusGeometry args={[0.3, 0.02, 8, 32]} />
                <meshStandardMaterial color={neonColor} emissive={neonColor} emissiveIntensity={2} />
            </mesh>

            {/* Head */}
            <mesh position={[0, 0.95, 0]} castShadow>
                <boxGeometry args={[0.2, 0.15, 0.2]} />
                <meshStandardMaterial color="#333" />
            </mesh>
            <mesh position={[0, 0.95, 0.11]}>
                <planeGeometry args={[0.15, 0.05]} />
                <meshBasicMaterial color={isEnemy ? "#f00" : "#f09"} />
            </mesh>

            {/* Legs */}
            <mesh ref={leftLeg} position={[-0.15, 0.2, 0]} castShadow>
                <cylinderGeometry args={[0.05, 0.02, 0.4]} />
                <meshStandardMaterial color="#222" />
            </mesh>
            <mesh ref={rightLeg} position={[0.15, 0.2, 0]} castShadow>
                <cylinderGeometry args={[0.05, 0.02, 0.4]} />
                <meshStandardMaterial color="#222" />
            </mesh>
        </group>
    );
};

const Wizard = ({ isEnemy }: CharacterProps) => {
    const group = useRef<THREE.Group>(null);
    useFrame((state) => {
        if (group.current) {
            group.current.position.y = Math.sin(state.clock.elapsedTime * 2) * 0.05;
        }
    });

    return (
        <group ref={group}>
            {/* Robe */}
            <mesh position={[0, 0.7, 0]} castShadow>
                <coneGeometry args={[0.3, 1.4, 8]} />
                <meshStandardMaterial color={isEnemy ? "#404" : "#224"} />
            </mesh>
            {/* Hat */}
            <mesh position={[0, 1.6, 0]} rotation={[-0.2, 0, 0]}>
                <coneGeometry args={[0.2, 0.6, 8]} />
                <meshStandardMaterial color={isEnemy ? "#202" : "#112"} />
            </mesh>
            {/* Staff */}
            <group position={[0.3, 0.8, 0.2]} rotation={[0.2, 0, 0.2]}>
                <mesh position={[0, 0, 0]}>
                    <cylinderGeometry args={[0.02, 0.02, 1.5]} />
                    <meshStandardMaterial color="#532" />
                </mesh>
                <mesh position={[0, 0.8, 0]}>
                    <icosahedronGeometry args={[0.08]} />
                    <meshStandardMaterial color={isEnemy ? "#f00" : "#0ff"} emissive={isEnemy ? "#f00" : "#0ff"} emissiveIntensity={2} />
                </mesh>
            </group>
        </group>
    );
};

const Archer = ({ isEnemy }: CharacterProps) => {
    const group = useRef<THREE.Group>(null);
    return (
        <group ref={group}>
            {/* Tunic */}
            <mesh position={[0, 0.7, 0]} castShadow>
                <boxGeometry args={[0.4, 0.7, 0.25]} />
                <meshStandardMaterial color={isEnemy ? "#431" : "#251"} />
            </mesh>
            {/* Hood */}
            <mesh position={[0, 1.2, 0]}>
                <boxGeometry args={[0.3, 0.3, 0.3]} />
                <meshStandardMaterial color={isEnemy ? "#321" : "#130"} />
            </mesh>
            {/* Bow (Torus segment) */}
            <mesh position={[0.3, 0.9, 0]} rotation={[0, 0, 0.5]}>
                <torusGeometry args={[0.3, 0.02, 4, 12, 2.5]} />
                <meshStandardMaterial color="#642" />
            </mesh>
        </group>
    );
};
